'use client';

import React from 'react';
import { Hand, Sparkles, Filter, X } from 'lucide-react';
import type { CallTag, TagSource } from '@/lib/api/types';

/**
 * Чип тега.
 *
 * Источник показан иконкой, а не сведён в один вид: доверие к тегу от него
 * и зависит. Человек поставил — это факт; правило сработало — проверяемо;
 * модель предположила — с уверенностью, которую видно в подсказке.
 */
const sources: Record<TagSource, { icon: React.ReactNode; title: string }> = {
  manual: { icon: <Hand size={9} />, title: 'Поставил человек' },
  rule: { icon: <Filter size={9} />, title: 'Сработало правило' },
  model: { icon: <Sparkles size={9} />, title: 'Предположила модель' },
};

/**
 * Цвет тега приходит от клиента и может оказаться любым, поэтому фон делаем
 * полупрозрачным, а текст берём тот же цвет: так чип читается и на светлой
 * теме, и на тёмной, не завися от удачности выбранного оттенка.
 */
export default function TagChip({ tag, onRemove }: { tag: CallTag; onRemove?: () => void }) {
  const source = sources[tag.source];
  const confidence =
    tag.source === 'model' && typeof tag.confidence === 'number'
      ? ` · уверенность ${Math.round(tag.confidence * 100)}%`
      : '';

  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] rounded-full pl-1.5 pr-2 py-0.5 border"
      style={{
        color: tag.color,
        borderColor: `${tag.color}55`,
        backgroundColor: `${tag.color}14`,
      }}
      title={`${source.title}${confidence}${tag.createdByName ? ` · ${tag.createdByName}` : ''}`}
    >
      <span className="opacity-70">{source.icon}</span>
      {tag.name}
      {onRemove && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="opacity-60 hover:opacity-100"
          title="Снять тег"
        >
          <X size={10} />
        </button>
      )}
    </span>
  );
}
